// ── Billing page decisions (BUG-021 #1, #2) ────────────────────────────────
// Pure logic behind BillingPage so it can be unit-tested without a DOM:
//   - which action each plan card offers (existing subscribers go to the
//     Stripe billing portal, never to a second checkout; a lower tier is never
//     labelled "Upgrade");
//   - loading the billing profile, surfacing read failures instead of
//     silently rendering the tenant as explorer.
// Display only: the server (checkout-session 409, stripe-webhooks) enforces.

export type BillingTier = 'explorer' | 'professional' | 'team' | 'enterprise';

const TIER_RANK: Readonly<Record<BillingTier, number>> = { explorer: 0, professional: 1, team: 2, enterprise: 3 };

export interface PlanCard {
  readonly tier: BillingTier;
  readonly priceId?: string;
}

export interface BillingProfile {
  readonly tier: BillingTier;
  /** True when a Stripe subscription is linked — plan changes then go through the portal. */
  readonly hasSubscription: boolean;
  /** True when a Stripe customer exists — the "Manage Subscription" portal button is shown. */
  readonly hasBillingAccount: boolean;
  readonly paymentPastDue: boolean;
}

export type PlanAction =
  | { readonly kind: 'current' }
  | { readonly kind: 'checkout'; readonly label: 'Upgrade' }
  | { readonly kind: 'portal'; readonly label: 'Upgrade' | 'Downgrade' }
  | { readonly kind: 'contact_sales' }
  | { readonly kind: 'none' };

export function planAction(plan: PlanCard, current: Pick<BillingProfile, 'tier' | 'hasSubscription'>): PlanAction {
  if (plan.tier === current.tier) return { kind: 'current' };
  if (plan.tier === 'enterprise') return { kind: 'contact_sales' };

  const isUpgrade = TIER_RANK[plan.tier] > TIER_RANK[current.tier];
  if (current.hasSubscription) {
    return { kind: 'portal', label: isUpgrade ? 'Upgrade' : 'Downgrade' };
  }
  return isUpgrade && plan.priceId ? { kind: 'checkout', label: 'Upgrade' } : { kind: 'none' };
}

/** Row shape selected from public.tenants (display copy; RLS select-own). */
export interface TenantBillingRow {
  readonly subscription_tier: unknown;
  readonly stripe_customer_id: unknown;
  readonly stripe_subscription_id: unknown;
  readonly payment_past_due: unknown;
}

/** Columns BillingPage reads; the fetcher passed to loadBillingProfile selects these. */
export const BILLING_PROFILE_COLUMNS = 'subscription_tier, stripe_customer_id, stripe_subscription_id, payment_past_due';

/** Loads the tenant's row (keeps this module free of the Supabase client for tests). */
export type BillingRowFetcher = (
  tenantId: string,
) => PromiseLike<{ readonly data: TenantBillingRow | null; readonly error: { readonly message: string } | null }>;

export type BillingRedirectResult =
  | { readonly kind: 'redirect'; readonly url: string }
  | { readonly kind: 'subscription_exists' }
  | { readonly kind: 'error'; readonly message: string };

const REQUEST_ERROR = 'Billing is temporarily unavailable. Please try again in a moment.';

/**
 * Call a checkout/portal endpoint and classify the outcome. Never throws: a
 * network failure or a non-JSON body (e.g. a gateway error page) becomes an
 * error result, so the page can always re-enable its buttons and show why.
 */
export async function requestBillingRedirect(send: () => Promise<Response>): Promise<BillingRedirectResult> {
  let res: Response;
  let body: { error?: unknown; url?: unknown; code?: unknown } | null;
  try {
    res = await send();
    body = (await res.json()) as typeof body;
  } catch {
    return { kind: 'error', message: REQUEST_ERROR };
  }
  if (res.status === 409 && body?.code === 'subscription_exists') return { kind: 'subscription_exists' };
  if (!res.ok) return { kind: 'error', message: typeof body?.error === 'string' ? body.error : REQUEST_ERROR };
  if (typeof body?.url !== 'string' || !body.url.startsWith('https://')) return { kind: 'error', message: REQUEST_ERROR };
  return { kind: 'redirect', url: body.url };
}

function isBillingTier(value: unknown): value is BillingTier {
  return value === 'explorer' || value === 'professional' || value === 'team' || value === 'enterprise';
}

const LOAD_ERROR = "We couldn't load your billing details, so plan changes are unavailable right now. Please refresh the page.";

/**
 * Load the signed-in tenant's billing profile.
 * @throws Error with a user-facing message when the read fails, returns no row,
 *   or returns an unrecognised tier.
 */
export async function loadBillingProfile(fetchRow: BillingRowFetcher, tenantId: string): Promise<BillingProfile> {
  let result: Awaited<ReturnType<BillingRowFetcher>>;
  try {
    result = await fetchRow(tenantId);
  } catch {
    throw new Error(LOAD_ERROR);
  }
  const { data, error } = result;
  if (error || !data || !isBillingTier(data.subscription_tier)) {
    throw new Error(LOAD_ERROR);
  }
  return {
    tier: data.subscription_tier,
    hasSubscription: typeof data.stripe_subscription_id === 'string' && data.stripe_subscription_id.length > 0,
    hasBillingAccount: typeof data.stripe_customer_id === 'string' && data.stripe_customer_id.length > 0,
    paymentPastDue: data.payment_past_due === true,
  };
}
